"use client";
import { useTranslations } from "@zapier/i18n";

import { useRef, useState } from "react";
import { ShareRole } from "@zapier/bff";
import { Button, Text } from "@zapier/design-system";
import { PermissionAssetType } from "@zapier/bff";
import { AddShareTypeahead, ShareSubject } from "./AddShareTypeahead";
import { ShareAccessSelect } from "./ShareAccessSelect";
import styles from "./AddShareForm.module.css";

export interface AddShareFormProps {
  allowSharingWithEveryone: boolean;
  allowSharingWithAdmins: boolean;
  allowSharingWithTeams: boolean;
  assetId: string;
  assetType: PermissionAssetType;
  label?: string;
  onShare?: (parameters: { role: ShareRole; subject: ShareSubject }) => void;
  roles: ShareRole[];
  disabledUserIds?: string[];
  /** Search members/teams on this account (e.g. parent org when sharing from a workspace). */
  searchAccountId?: string;
  /** When set, "Everyone at …" / account-admin rows use this account (e.g. parent org on the Organization tab). */
  everyoneOptionAccount?: {
    id: number;
    name?: string | null;
    logoUrl?: string | null;
  };
}

export function AddShareForm({
  allowSharingWithEveryone,
  allowSharingWithAdmins,
  allowSharingWithTeams,
  label = allowSharingWithTeams ? "Add member or team" : "Add member",
  disabledUserIds,
  onShare,
  roles,
  searchAccountId,
  everyoneOptionAccount,
}: AddShareFormProps) {
  const t = useTranslations("add-share-form");

  const formReference = useRef<HTMLFormElement>(null);
  const [subject, setSubject] = useState<ShareSubject | undefined>();
  const [role, setRole] = useState<ShareRole | undefined>(roles[0]);

  const onSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    // This should not happen, but verify that all required fields are set
    if (!subject || !role) {
      return;
    }

    onShare?.({ role, subject });

    // Clear the form fields after submission
    formReference.current?.reset();
  };

  return (
    <form onSubmit={onSubmit} ref={formReference}>
      <div className={styles.root}>
        <div className={styles.fields}>
          <div className={styles.typeahead}>
            <AddShareTypeahead
              allowSharingWithEveryone={allowSharingWithEveryone}
              allowSharingWithAdmins={allowSharingWithAdmins}
              allowTeamSearch={allowSharingWithTeams}
              disabledUserIds={disabledUserIds}
              label={label}
              onSelectionChange={setSubject}
              searchAccountId={searchAccountId}
              everyoneOptionAccount={everyoneOptionAccount}
            />
          </div>

          {/* Hide the role picker if there's only one role available */}
          {roles.length > 1 && (
            <div
              className={styles.accessSelect}
              data-testid="share-role-select"
            >
              <ShareAccessSelect
                onSelectionChange={(role: ShareRole) => setRole(role)}
                roles={roles}
                selectedRole={role}
              />
            </div>
          )}

          <Button
            isDisabled={!subject || !role}
            type="submit"
            variant="primary"
          >
            {t("buttons.share")}
          </Button>
        </div>

        <Text color="TextWeaker" tag="p" type="MinimalPrint1Multiline">
          {t("messages.default-access-info")}
        </Text>
      </div>
    </form>
  );
}
